import { AuthenticationEntity } from "@modules/auth/entities/auth.entity";
import { API_HEADER, BASE_URL } from "@modules/shared/enums/common-enum";
import { fetchWithAuth } from "@modules/shared/helpers/fetch-with-auth.helper";
import { TrainerPackageModel } from "../models/TrainerPackageModel";

export type PendingPackagesListResponse = {
  data: TrainerPackageModel[];
  pagination: { total: number };
};

const normalizeCategory = (cat: string): string => {
  return cat.trim().toLowerCase().replace(/\s+/g, " ");
};

export const PackageService = {
  getPendingPackages: async (
    auth: AuthenticationEntity,
    setAuth: (auth: AuthenticationEntity | null) => void,
    logout: () => void,
    page: number = 1,
    limit: number = 10,
    filters: Record<string, any> = {},
  ): Promise<PendingPackagesListResponse | null> => {
    const params: Record<string, string> = {
      page: String(page),
      limit: String(limit),
    };

    const filterKeys = Object.keys(filters) as (keyof typeof filters)[];
    filterKeys.forEach((key) => {
      const value = filters[key];
      if (value === "" || value === undefined || value === null) return;
      if (key !== "search" && key !== "category") {
        params[key] =
          typeof value === "boolean" ? String(value) : String(value);
      }
    });

    let url: string;
    const searchValue = filters.search?.trim();
    const categoryValue = filters.category?.trim();

    if (searchValue && searchValue !== "") {
      const searchParams = new URLSearchParams({
        q: searchValue,
        page: String(page),
        limit: String(limit),
      });
      if (filters.verified_package !== undefined) {
        searchParams.append("verified", String(filters.verified_package));
      }
      if (filters.is_active !== undefined && filters.is_active !== "") {
        searchParams.append("is_active", String(filters.is_active));
      }
      url = `${BASE_URL}/trainer-packages/search?${searchParams.toString()}`;
    } else if (categoryValue && categoryValue !== "") {
      const normalizedCategory = normalizeCategory(categoryValue);
      const catParams = new URLSearchParams({
        package_category: normalizedCategory,
        page: String(page),
        limit: String(limit),
      });
      if (filters.verified_package !== undefined) {
        catParams.append("verified", String(filters.verified_package));
      }
      if (filters.is_active !== undefined && filters.is_active !== "") {
        catParams.append("is_active", String(filters.is_active));
      }
      url = `${BASE_URL}/trainer-packages/by-category?${catParams.toString()}`;
    } else {
      const query = new URLSearchParams(params).toString();
      url = `${BASE_URL}/admin/trainer-packages?${query}`;
    }

    const res = await fetchWithAuth(
      url,
      {
        method: "GET",
        headers: { ...API_HEADER, "x-platform": "web" },
      },
      auth,
      setAuth,
      logout,
    );

    if (res && res.data && Array.isArray(res.data)) {
      if (res.pagination && typeof res.pagination.total === "number") {
        return {
          data: res.data,
          pagination: res.pagination,
        };
      }
      const total = res.data.length;
      return {
        data: res.data,
        pagination: { total },
      };
    }
    return null;
  },

  getPackagesByTrainerId: async (
    auth: AuthenticationEntity,
    setAuth: (auth: AuthenticationEntity | null) => void,
    logout: () => void,
    id: string,
  ) => {
    const url = `${BASE_URL}/admin/trainer-packages/${id}`;
    const res = await fetchWithAuth(
      url,
      {
        method: "GET",
        headers: { ...API_HEADER, "x-platform": "web" },
      },
      auth,
      setAuth,
      logout,
    );
    return res;
  },

  verifyPackage: async (
    auth: AuthenticationEntity,
    setAuth: (auth: AuthenticationEntity | null) => void,
    logout: () => void,
    id: string,
  ) => {
    const url = `${BASE_URL}/admin/trainer-packages/verify/${id}`;
    const res = await fetchWithAuth(
      url,
      {
        method: "POST",
        headers: { ...API_HEADER, "x-platform": "web" },
        body: JSON.stringify({ verified_package: true }),
      },
      auth,
      setAuth,
      logout,
    );
    return res;
  },

  rejectPackage: async (
    auth: AuthenticationEntity,
    setAuth: (auth: AuthenticationEntity | null) => void,
    logout: () => void,
    id: string,
    reason: string,
  ) => {
    // If the API actually rejects it, change the URL and method.
    const url = `${BASE_URL}/admin/trainer-packages/reject/${id}`; //API Demo
    const res = await fetchWithAuth(
      url,
      {
        method: "POST",
        headers: { ...API_HEADER, "x-platform": "web" },
        body: JSON.stringify({ reason }),
      },
      auth,
      setAuth,
      logout,
    );
    return res;
  },
};
